/**
 * APTableHeaderCell Component
 *
 * Table header cell component with sorting functionality, accessibility features,
 * and keyboard navigation. Extracted from the main APTable component.
 *
 * @example
 * ```tsx
 * <APTableHeaderCell
 *   column={{
 *     key: 'name',
 *     label: 'Name',
 *     accessor: 'name',
 *     sortable: true,
 *     align: 'left'
 *   }}
 *   sort={{ column: 'name', direction: 'asc' }}
 *   onSortChange={(sort) => console.log(sort)}
 * />
 * ```
 */

import { forwardRef, useCallback } from 'react';

import type { IAPTableHeaderCellProps, IAPTableSort } from '../../types';

import './styles.scss';

/**
 * APTableHeaderCell - Table header cell with sorting
 *
 * Handles column headers with optional sorting functionality,
 * accessibility features, and keyboard navigation.
 */
export const APTableHeaderCell = forwardRef<HTMLTableCellElement, IAPTableHeaderCellProps>(
  <TData,>(
    {
      column,
      sort,
      onSortChange,
      className = '',
      'data-testid': testId = 'ap-table-header-cell',
      ...rest
    }: IAPTableHeaderCellProps<TData> & { 'data-testid'?: string },
    ref: React.Ref<HTMLTableCellElement>
  ) => {
    const isSorted = sort?.column === column.key;
    const sortDirection = isSorted ? sort?.direction : undefined;

    const handleSort = useCallback(() => {
      if (!column.sortable || !onSortChange) return;

      let newSort: IAPTableSort | null = null;

      if (!isSorted) {
        // First click - sort ascending
        newSort = { column: column.key, direction: 'asc' };
      } else if (sortDirection === 'asc') {
        // Second click - sort descending
        newSort = { column: column.key, direction: 'desc' };
      }
      // Third click - clear sort (newSort remains null)

      onSortChange(newSort);
    }, [column.key, column.sortable, isSorted, sortDirection, onSortChange]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (!column.sortable || !onSortChange) return;

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleSort();
        }
      },
      [column.sortable, onSortChange, handleSort]
    );

    // Build header classes
    const headerClasses = [
      'ap-table__header-cell',
      `ap-table__header-cell--${column.align || 'left'}`,
      column.sortable && 'ap-table__header-cell--sortable',
      isSorted && `ap-table__header-cell--sorted ap-table__header-cell--sorted-${sortDirection}`,
      column.headerClassName,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Render header content using custom renderer or default label
    const headerContent = column.renderHeader ? column.renderHeader(column) : column.label;

    return (
      <th
        ref={ref}
        className={headerClasses}
        style={{
          width: column.width,
          minWidth: column.minWidth,
          maxWidth: column.maxWidth,
        }}
        onClick={column.sortable ? handleSort : undefined}
        onKeyDown={column.sortable ? handleKeyDown : undefined}
        role={column.sortable ? 'button' : 'columnheader'}
        tabIndex={column.sortable ? 0 : undefined}
        aria-sort={
          isSorted
            ? sortDirection === 'asc'
              ? 'ascending'
              : 'descending'
            : column.sortable
              ? 'none'
              : undefined
        }
        data-testid={testId}
        {...rest}
      >
        <div className="ap-table__header-cell-content">
          <span className="ap-table__header-cell-label">{headerContent}</span>
        </div>
      </th>
    );
  }
) as <TData = Record<string, unknown>>(
  props: IAPTableHeaderCellProps<TData> & {
    ref?: React.Ref<HTMLTableCellElement>;
    'data-testid'?: string;
  }
) => React.ReactElement;

export default APTableHeaderCell;
